import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { ErrorState } from '@/components/common/ErrorState';
import { PageLoader } from '@/components/common/PageLoader';
import { pcmpMisStatusReportQueryOptions } from '../api/pcmpMisStatusReport.api';
import { PcmpPrintSheet } from '../components/PcmpPrint/PcmpPrintSheet';
import { PcmpMisStatusReportSheet } from '../components/PcmpReports/PcmpMisStatusReportSheet';
import { usePcmpMisStatusSubStatus } from '../hooks/usePcmpMisStatusSubStatus';
import type { PcmpMisStatusReportParams } from '../types/pcmpMisStatusReport.types';

const route = getRouteApi('/_print/pcmp/reports/mis-status/print');

/** The MIS Status report on landscape A4 sheets, opening the print dialog once loaded. */
export function PcmpMisStatusReportPrintPage() {
  const search = route.useSearch();
  const { projectId, division, departmentId, dateFrom, dateTo, misUserId } = search;
  const { subStatus, isReady } = usePcmpMisStatusSubStatus(search.subStatus);

  const params = useMemo<PcmpMisStatusReportParams>(
    () => ({ projectId, division, departmentId, dateFrom, dateTo, misUserId, subStatus }),
    [projectId, division, departmentId, dateFrom, dateTo, misUserId, subStatus],
  );

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    ...pcmpMisStatusReportQueryOptions(params),
    enabled: isReady,
    // Closing the print dialog refocuses the tab; keep the sheet as printed.
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (data) document.title = `PCMP MIS Status Report ${data.reportDate}`;
  }, [data]);

  if (isLoading) return <PageLoader />;

  if (error || !data) {
    return (
      <div className="p-6">
        <ErrorState
          error={error}
          fallbackTitle="Could not load the MIS Status report"
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          fill
        />
      </div>
    );
  }

  return (
    <PcmpPrintSheet>
      <PcmpMisStatusReportSheet report={data} />
    </PcmpPrintSheet>
  );
}
